import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { titleCase } from "../lib/nutrition";
import { saveCustomIngredient } from "../lib/api";

const EMPTY_NUTRIENTS = {
  calories: 0,
  protein: 0,
  carbs: 0,
  sugars: 0,
  fat: 0,
  saturated_fat: 0,
  fiber: 0,
  sodium_mg: 0,
};

const MACRO_FIELDS = [
  { key: "calories", label: "Calories (kcal)", step: "1" },
  { key: "protein", label: "Protéines (g)", step: "0.1" },
  { key: "carbs", label: "Glucides (g)", step: "0.1" },
  { key: "sugars", label: "dont sucres (g)", step: "0.1" },
  { key: "fat", label: "Lipides (g)", step: "0.1" },
  { key: "saturated_fat", label: "AG saturés (g)", step: "0.1" },
  { key: "fiber", label: "Fibres (g)", step: "0.1" },
  { key: "sodium_mg", label: "Sodium (mg)", step: "1" },
];

/**
 * Unified dialog used for:
 *  - creating a brand new custom ingredient (no USDA source)
 *  - editing the French name of any ingredient
 *  - editing the macros of a custom ingredient
 *
 * Props:
 *   mode: "create" | "edit"
 *   ingredient (edit): existing ingredient object to modify
 *   onSubmit(patchOrNew): called with new ingredient (create) or patch (edit)
 */
export const IngredientEditDialog = ({
  open,
  onOpenChange,
  mode,
  ingredient,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const isCustom = isCreate || !!ingredient?.custom;

  const [nameFr, setNameFr] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(100);
  const [nutrients, setNutrients] = useState(EMPTY_NUTRIENTS);
  const [memorize, setMemorize] = useState(true);
  const [savingLibrary, setSavingLibrary] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setNameFr("");
      setDescription("");
      setQuantity(100);
      setNutrients({ ...EMPTY_NUTRIENTS });
      setMemorize(true);
    } else if (ingredient) {
      setNameFr(ingredient.name_fr || "");
      setDescription(ingredient.description || "");
      setQuantity(ingredient.quantity_g ?? 100);
      setNutrients({ ...EMPTY_NUTRIENTS, ...(ingredient.nutrients_per_100g || {}) });
    }
  }, [open, isCreate, ingredient]);

  const setMacro = (key, value) => {
    setNutrients((n) => ({ ...n, [key]: parseFloat(value) || 0 }));
  };

  const canSubmit = isCreate
    ? nameFr.trim().length > 0
    : nameFr.trim().length > 0 || description.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (isCreate) {
      const finalName = nameFr.trim();
      if (memorize) {
        setSavingLibrary(true);
        try {
          await saveCustomIngredient({
            name_fr: finalName,
            nutrients_per_100g: nutrients,
          });
        } catch {
          // silent — adding to recipe still works
        } finally {
          setSavingLibrary(false);
        }
      }
      onSubmit({
        id: crypto.randomUUID(),
        fdc_id: null,
        custom: true,
        description: finalName,
        brand: null,
        name_fr: finalName,
        quantity_g: parseFloat(quantity) || 0,
        quid: false,
        nutrients_per_100g: nutrients,
      });
    } else {
      const patch = {
        name_fr: nameFr.trim() || null,
      };
      if (isCustom) {
        patch.description = nameFr.trim() || description;
        patch.nutrients_per_100g = nutrients;
      }
      onSubmit(patch);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl p-0 overflow-hidden gap-0"
        data-testid="ingredient-edit-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            {isCreate ? (
              <>
                <Plus className="h-5 w-5 text-emerald-700" />
                Ingrédient personnalisé
              </>
            ) : (
              <>
                <Pencil className="h-5 w-5 text-emerald-700" />
                {isCustom ? "Modifier l'ingrédient" : "Renommer en français"}
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {isCreate
              ? "Saisissez les valeurs nutritionnelles pour 100 g. Idéal quand USDA ne propose pas l'ingrédient ou pour un produit interne."
              : isCustom
              ? "Modifiez le nom et/ou les valeurs nutritionnelles."
              : "Ajustez le libellé affiché sur l'étiquette INCO (ex : « Bavette de bœuf » au lieu de « Beef, brisket »)."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto scrollbar-thin">
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-1.5">
              Nom (français) <span className="text-red-500 normal-case">*</span>
            </label>
            <Input
              value={nameFr}
              onChange={(e) => setNameFr(e.target.value)}
              placeholder="Ex : Bavette de bœuf, Pâte de curry maison…"
              className="focus-visible:ring-emerald-500"
              data-testid="ingredient-name-fr-input"
              autoFocus
            />
            {!isCreate && !isCustom && (
              <p className="text-[11px] text-stone-500 mt-1.5">
                Source USDA : <span className="italic">{titleCase(description)}</span> · FDC #{ingredient?.fdc_id}
              </p>
            )}
          </div>

          {isCreate && (
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-1.5">
                Quantité initiale (g)
              </label>
              <Input
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="focus-visible:ring-emerald-500 w-32"
                data-testid="ingredient-qty-input"
              />
            </div>
          )}

          {isCustom && (
            <div className="pt-2 border-t border-stone-100">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500">
                  Valeurs nutritionnelles pour 100 g
                </div>
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
                  Personnalisé
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3" data-testid="custom-macros-grid">
                {MACRO_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="block text-[11px] text-stone-600 mb-1">{f.label}</label>
                    <Input
                      type="number"
                      min="0"
                      step={f.step}
                      value={nutrients[f.key]}
                      onChange={(e) => setMacro(f.key, e.target.value)}
                      className="h-9 focus-visible:ring-emerald-500"
                      data-testid={`macro-${f.key}-input`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {isCreate && (
          <div className="px-6 py-3 border-t border-stone-100 bg-emerald-50/40">
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="memorize-row">
              <input
                type="checkbox"
                checked={memorize}
                onChange={(e) => setMemorize(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500"
                data-testid="memorize-checkbox"
              />
              <span className="text-sm text-stone-700">
                <span className="font-semibold">Mémoriser dans ma bibliothèque</span>
                <span className="block text-xs text-stone-500 mt-0.5">
                  Réutilisable d'un clic dans vos prochaines recettes (sans ressaisir les macros).
                </span>
              </span>
            </label>
          </div>
        )}

        <DialogFooter className="px-6 py-3 border-t border-stone-100 bg-stone-50/40">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-stone-600"
            data-testid="ingredient-edit-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || savingLibrary}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="ingredient-edit-submit"
          >
            {isCreate ? "Ajouter" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IngredientEditDialog;
